import axios from 'axios';
import authHeader from '@/backend/auth/auth-header';
import {
  applyBearerHeader,
  createRefreshGate,
  executeRefreshAttempt,
  extractBearerToken,
  planAfterRefreshFailure,
  planUnauthorizedRecovery,
  refreshUnderCrossTabLock,
} from '@/backend/auth/refreshLogic.mjs'
import { prepareFormDataRequest } from '@/backend/auth/formDataHeaders.mjs'
import { humanizeAxiosError, isCancelError } from '@/backend/errorMessages'
import { rememberError } from '@/backend/errorLog.mjs'
import {
  clearImpersonationSession,
  clearStoredAuth,
  getAccessToken,
  getRefreshToken,
  isImpersonationTab,
  writeStoredAuth,
} from '@/backend/auth/tokenStorage.mjs'

const baseURL = process.env.VUE_APP_BASE_URL;
const isDev = process.env.NODE_ENV === 'development';

const path = {
	api:'/api/v2/',
	task:'/tasks/',
	user:'/auth/',
	base:'',
	admin:'/admin/',
	tms:'/tms/',
	trips:'/api/v2/trips/',
	cars:'/api/v2/cars',
	drivers:'/api/v2/drivers',
	managers:'/api/v2/managers',
	companies:'/api/v2/companies',
	executors:'/api/v2/executors',
	usersAdmin:'/api/v2/users',
	security_options:'/api/v2/security-options',
	applications:'/api/v2/applications'
}

async function postRefresh(refreshToken) {
  const url = (isDev ? '' : (baseURL || '')) + '/auth/refresh'
  const { data } = await axios.post(
    url,
    { refresh_token: refreshToken },
    { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } },
  )
  return data
}

// Refresh: single-flight в пределах окна + кросс-оконный лок (Web Locks).
// Одновременно во всём браузере refresh делает одно окно; остальные после
// ожидания лока забирают ротированную пару из общего localStorage.
const refreshOnce = createRefreshGate(() => {
  const observedRefreshToken = getRefreshToken()
  return refreshUnderCrossTabLock({
    locks: globalThis.navigator?.locks,
    observedRefreshToken,
    doRefresh: () =>
      executeRefreshAttempt({
        getRefreshToken,
        getAccessToken,
        writeStoredAuth,
        postRefresh,
      }),
    getRefreshToken,
    getAccessToken,
  })
})

export function attachAuthInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config || {}
      const plan = planUnauthorizedRecovery({
        status: error.response?.status,
        alreadyRetried: !!original._retry,
        requestAccess: extractBearerToken(original.headers),
        latestAccess: getAccessToken(),
        refreshToken: getRefreshToken(),
        requestUrl: original.url || '',
        impersonating: isImpersonationTab(),
      })

      if (plan.action === 'retry_with_access') {
        updateHeader(instance)
        return instance.request(applyBearerHeader(original, plan.accessToken))
      }
      if (plan.action !== 'refresh') {
        // Сессия имперсонализации истекла — завершаем её, а не подменяем
        // личность токеном админа из localStorage.
        if (error.response?.status === 401 && isImpersonationTab()) {
          clearImpersonationSession()
          location.replace('/login')
        }
        return Promise.reject(error)
      }

      try {
        const pair = await refreshOnce()
        updateHeader(instance)
        return instance.request(applyBearerHeader(original, pair.access_token))
      } catch (refreshError) {
        const after = planAfterRefreshFailure({
          attemptedRefresh: refreshError?._used_refresh ?? null,
          currentRefresh: getRefreshToken(),
          currentAccess: getAccessToken(),
        })
        if (after.action === 'clear_and_reject') {
          clearStoredAuth()
        } else if (after.action === 'retry_with_access') {
          updateHeader(instance)
          return instance.request(applyBearerHeader(original, after.accessToken))
        }
        return Promise.reject(refreshError)
      }
    },
  )
  return instance
}

// После auth-интерсептора (refresh/повтор): к компонентам ошибка приходит уже
// с русским error.message.
export function attachHumanErrorInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const human = humanizeAxiosError(error)
      // Последние ошибки — в контекст кнопки «Сообщить в IT».
      if (!isCancelError(human)) rememberError(human)
      return Promise.reject(human)
    },
  )
  return instance
}

export function useAuthAxios(options = {}) {
	const instance = axios.create({
      ...options,
      headers: authHeader(),
    })
	// Authorization пересчитывается на каждый запрос из актуального хранилища,
	// чтобы вкладка имперсонализации брала токен из sessionStorage сразу после handoff.
	instance.interceptors.request.use((config) => {
		const h = authHeader()
		if (h.Authorization) {
			config.headers.Authorization = h.Authorization
		}
		// authHeader() ставит application/json на инстанс. Для FormData это
		// ломает multipart: FastAPI не видит file → "file: field required".
		return prepareFormDataRequest(config)
	})
  return attachHumanErrorInterceptor(attachAuthInterceptor(instance))
}

export function useAxiosCreate(name='base') {
	let url = isDev ? '' : baseURL
	if(name != 'base') url = (isDev ? '' : baseURL) + path[name]
	return useAuthAxios({ baseURL: url })
}

export function updateHeader(instance) {
	instance.defaults.headers.common = authHeader();
}
